import DocumentEvents from 'react-document-events'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useMutationObserver } from '../../../app/hooks/useMutationObserver'
import { useWindowSize } from 'the-platform'
import './Modal.less'
import { isSSR } from 'shared/helpers'

export type modalWidth = 'MEDIUM' | 'WIDE' | 'SUPERWIDE' | 'FLUID'

export interface IModalProps {
  show?: boolean
  align?: 'left' | 'center' | 'right'
  children?: any
  onHide?: (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  onHidden?: () => void
  onShown?: () => void
  header?: any
  subHeader?: React.ReactElement<any> | string
  controls?:
    | (() => JSX.Element)
    | JSX.Element
    | JSX.Element[]
    | React.ReactNode[]
  className?: string
  dataTest?: string
  cannotBeHidden?: boolean
  size?: modalWidth
  newDesign?: boolean // This is a toggle for the new variation of Modal
}

/**
 * our general modal. TODO we need to fix the onHide hooks and polish the API. Ideally introduce our own wrapper for chakra modal.
 */
export const Modal: FC<IModalProps> = (props) => {
  const overlayEl = useRef<HTMLDivElement | null>()
  const controlsEl = useRef<HTMLDivElement>(null)
  const windowSize = !isSSR && useWindowSize()
  const [modalHeight, setModalHeight] = useState(0)

  const headerRef = useRef<HTMLHeadingElement | null>()

  const modalDivRef = useMutationObserver<HTMLDivElement>(
    (_mutations, el) => {
      return setTimeout(() => {
        setModalHeight(el.clientHeight)
      }, 501) // we need timeout this high because there are animations which take up to 500 ms
    },
    {
      subtree: true,
      childList: true
    }
  )

  const cssClasses = ['Modal']
  if (props.align) {
    cssClasses.push(`Modal--${props.align}`)
  }
  if (props.className) {
    cssClasses.push(props.className)
  }
  if (props.size) {
    cssClasses.push(`Modal--${props.size}`)
  }
  if (props.newDesign) {
    cssClasses.push(`Modal--v2`)
  }

  const isTallerThanScreen = windowSize && modalHeight > windowSize.height

  useEffect(() => {
    props.show !== false && props.onShown && props.onShown()
  }, [props.show])

  return (
    <div
      className="Modal-overlay"
      ref={(el) => (overlayEl.current = el)}
      // we use onMouseDown rather than onClick, because with onClick user can very easily accidentally close the modal trying to select  input contents inside the modal
      onMouseDown={
        props.cannotBeHidden
          ? undefined
          : (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              const onHide = props.onHide

              if (e.target === overlayEl.current && props.show) {
                onHide && onHide(e)
              }
            }
      }
      data-show={props.show}
      onTransitionEnd={(e) => {
        // TODO this is not reliable, use the onHide with caution. Ideally don't use this component for anything new
        const isOverlay = e.target === overlayEl.current

        if (isOverlay) {
          const { onHidden } = props

          if (e.propertyName === 'opacity' && !props.show) {
            onHidden && onHidden()
          }
        }
      }}
    >
      <DocumentEvents
        onKeyUp={(ev) => {
          if (ev.key === 'Escape' && props.show) {
            props.onHide && props.onHide(ev)
            ev.stopPropagation()
            return false
          }
          if (ev.key === 'Enter' && props.show) {
            const target: HTMLElement = ev.target

            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
              return // we don't wan to close the modal when use has focus on an input
            }
            const buttons = controlsEl.current?.getElementsByClassName('Button')
            if (buttons) {
              // @ts-expect-error
              buttons[buttons.length - 1]?.click()
            }

            ev.stopPropagation()
            return false
          }
        }}
      />
      <div
        ref={(el) => {
          if (el) {
            modalDivRef.current = el
            setModalHeight(el.clientHeight)
          }
        }}
        className={cssClasses.join(' ')}
        data-test={props.dataTest}
        style={{
          marginTop: isTallerThanScreen ? 'auto' : undefined
        }}
      >
        {props.header && (
          <div className="Modal__header">
            {/* @ts-expect-errors */}
            <h3 ref={headerRef}>{props.header}</h3>
            {props.subHeader && (
              <div className="subHeader">{props.subHeader}</div>
            )}
          </div>
        )}
        <div className="Modal__body">{props.children}</div>
        {props.controls && (
          <div className="Modal__controls" ref={controlsEl}>
            {typeof props.controls === 'function'
              ? props.controls()
              : props.controls}
          </div>
        )}
      </div>
    </div>
  )
}
